import { DatePipe, DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSliderModule } from '@angular/material/slider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { LoggerModule } from 'ngx-logger';
import { NgxPaginationModule } from 'ngx-pagination';
import { environment } from '../environments/environment';
import { createErrorHandler } from './common/sentry';
import { CvPipelineDialogComponent } from './cv-pipeline-dialog/cv-pipeline-dialog.component';
import { AppComponent } from './app.component';
import {
  AuthGuardService,
  AuthGuardService as AuthGuard
} from './auth/auth.guard.service';
import { CallbackComponent } from './auth/callback.component';
import { TokenInterceptor } from './auth/http.interceptor';
import { MaintenanceInterceptor } from './common/maintenance.interceptor';
import { LoginComponent } from './auth/login.component';
import { DuplicateGameDialogComponent } from './duplicate-game-dialog/duplicate-game-dialog.component';
import { EventsComponent } from './events/events.component';
import { GameDetailsComponent } from './game-details/game-details.component';
import { GameDetailsEssentialsCardComponent } from './game-details/game-details-essentials-card/game-details-essentials-card.component';
import { GameDetailsVideosCardComponent } from './game-details/game-details-videos-card/game-details-videos-card.component';
import { LiveStreamSelectionComponent } from './live-stream-selection/live-stream-selection.component';
import { GameEventsComponent } from './game-events/game-events.component';
import { ShotImpactDialogComponent } from './game-events/shot-impact-dialog.component';
import { GameExportDialogComponent } from './game-export-dialog/game-export-dialog.component';
import { GameSyncDialogComponent } from './game-sync-dialog/game-sync-dialog.component';
import { GameValidationDialogComponent } from './game-validation-dialog/game-validation-dialog.component';
import { ExportWarningsDialogComponent } from './export-warnings-dialog/export-warnings-dialog.component';
import { GameModule } from './game/game.module';
import { GamesComponent } from './games/games.component';
import { BulkImportDialogComponent } from './games/bulk-import-dialog/bulk-import-dialog.component';
import { ImportSIHFGoalsDialogComponent } from './import-sihf-goals-dialog/import-sihf-goals-dialog.component';
import { LineupChangeDialogComponent } from './lineup-change-dialog/lineup-change-dialog.component';
import { PenaltiesComponent } from './penalties/penalties.component';
import { GameResolver } from './resolvers/game-resolver';
import { SeasonSyncDialogComponent } from './season-sync-dialog/season-sync-dialog.component';
import { ScheduleAutoAssignDialogComponent } from './schedule/schedule-auto-assign-dialog/schedule-auto-assign-dialog.component';
import { ScheduleAutoAssignResultDialogComponent } from './schedule/schedule-auto-assign-result-dialog/schedule-auto-assign-result-dialog.component';
import { ScheduleTeamLeaderAssignDialogComponent } from './schedule/schedule-team-leader-assign-dialog/schedule-team-leader-assign-dialog.component';
import { TeamLeaderAssignmentConfigDialogComponent } from './schedule/team-leader-assignment-config-dialog/team-leader-assignment-config-dialog.component';
import { ScheduleComponent } from './schedule/schedule.component';
import { ScheduleRosterDetailsComponent } from './schedule/schedule-roster-details/schedule-roster-details.component';
import { ScheduleEventHistoryDialogComponent } from './schedule/schedule-event-history-dialog/schedule-event-history-dialog.component';
import { AvailabilityOverviewPanelComponent } from './schedule/availability-overview/availability-overview-panel.component';
import { AvailabilityKpisComponent } from './schedule/availability-overview/availability-kpis.component';
import { CollectorAvailabilityListComponent } from './schedule/availability-overview/collector-availability-list.component';
import { CollectorAvailabilityRowComponent } from './schedule/availability-overview/collector-availability-row.component';
import { AvailabilityTimelineComponent } from './schedule/availability-overview/availability-timeline.component';
import { TeamAvailabilityListComponent } from './schedule/availability-overview/team-availability-list.component';
import { DcRosterConfigComponent } from './schedule/dc-roster-config/dc-roster-config.component';
import { DcRosterConfigDialogComponent } from './schedule/dc-roster-config/dc-roster-config-dialog/dc-roster-config-dialog.component';
import { DcRosterConfigEventDialogComponent } from './schedule/dc-roster-config/dc-roster-config-event-dialog/dc-roster-config-event-dialog.component';
import { AlertService } from './services/alert.service';
import { AppInitService } from './services/app.init.service';
import { EventValidationService } from './services/event-validation.service';
import { EventService } from './services/event.service';
import { GameTimeService } from './services/game-time.service';
import { GameService } from './services/game.service';
import { LeagueService } from './services/league.service';
import { TrackingService } from './services/tracking.service';
import { PlayerService } from './services/player.service';
import { StrengthStateService } from './services/strength-state.service';
import { TeamService } from './services/team.service';
import { SharedModule } from './shared/shared.module';
import { ShiftsOverviewComponent } from './shifts-overview/shifts-overview.component';
import { TeamInputComponent } from './team-input/team-input.component';
import { TimeOnIceComponent } from './time-on-ice/time-on-ice.component';
import { TimeOnIceModule } from './time-on-ice/time-on-ice.module';
import { UpdateFOINTDialogComponent } from './update-foint-dialog/update-foint-dialog.component';
import { UserComponent } from './user/user.component';
import { VideoDownloadDialogComponent } from './video-download-dialog/video-download-dialog.component';
import { VideoKeyboardActionsComponent } from './video-keyboard-actions/video-keyboard-actions.component';
import { VideoLiveScrubBarComponent } from './video-live-scrub-bar/video-live-scrub-bar.component';
import { VideoObjectTracksComponent } from './video-object-tracks/video-object-tracks.component';
import { VideoPlayerWindowComponent } from './video-player-window/video-player-window.component';
import { CvShowcaseVideoPlayerWindowComponent } from './cv-showcase-video-player-window/cv-showcase-video-player-window.component';
import { VideoTimeOffsetDialogComponent } from './video-time-offset-dialog/video-time-offset-dialog.component';
import { VideoTrackingControlsComponent } from './video-tracking-controls/video-tracking-controls.component';
import { VideoModule } from './video/video.module';
import { CustomPlayerInputComponent } from './custom-player-input/custom-player-input.component';
import { MaskitoModule } from '@maskito/angular';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarModule
} from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { LeagueSummaryComponent } from './league-summary/league-summary.component';
import { ShiftPanelComponent } from './visual-time-on-ice/shift-panel.component';
import { RolesGuard } from './auth/roles.guard';
import { VideoService } from './services/video.service';
import { PuckPossessionStateService } from './services/puck-possession-state.service';
import { TeamsComponent } from './teams/teams.component';
import { PlayersComponent } from './players/players.component';
import { ExternalIdsDialogComponent } from './shared/external-ids-dialog/external-ids-dialog.component';
import { EditPlayerDialogComponent } from './players/edit-player-dialog/edit-player-dialog.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { EditTeamDialogComponent } from './teams/edit-team-dialog/edit-team-dialog.component';
import { EditLeagueDialogComponent } from './league-summary/edit-league-dialog/edit-league-dialog.component';
import { SeasonService } from './services/season.service';
import { PenaltiesDetailsComponent } from './penalties/penalties-details/penalties-details.component';
import { AllPlayersInputComponent } from './all-players-input/all-players-input.component';
import { VideoPlayerTrimComponent } from './video-player-trim/video-player-trim.component';
import { VideoPlayerTrimControlsComponent } from './video-player-trim/video-player-trim-controls/video-player-trim-controls.component';
import { VideoPlayerTrimDialogComponent } from './video-player-trim/video-player-trim-dialog/video-player-trim-dialog.component';
import { VideoPlayerPreloadComponent } from './video-player-preload/video-player-preload.component';
import { LocalStorageEffects } from './state/effects/root.effects';
import {
  reducer as gameEventReducer,
  name as gameEventFeatureKey
} from './state/reducers/game-event.reducer';
import {
  reducer as gamerReducer,
  name as gameFeatureKey
} from './state/reducers/game.reducer';
import { VisualTimeOnIceComponent } from './visual-time-on-ice/visual-time-on-ice.component';
import { EventDetailsComponent } from './event-details/event-details.component';
import { ShiftDetailsComponent } from './shift-details/shift-details.component';
import { MultipleShiftActionsComponent } from './multiple-shift-actions/multiple-shift-actions.component';
import { GameActionMenuComponent } from './game-action-menu/game-action-menu.component';
import { GameQualityReportDialogComponent } from './game-quality-report-dialog/game-quality-report-dialog.component';
import { GameQualityDialogComponent } from './game-quality-dialog/game-quality-dialog.component';
import { TagFormFieldComponent } from './tag-form-field/tag-form-field.component';
import { GameEventShotComponent } from './game-events/game-event-shot/game-event-shot.component';
import { GameEventInterruptionTypeService } from './services/game-event-interruption-type.service';
import { OfficialInputComponent } from './official-input/official-input.component';
import { GameEventHighlightComponent } from './game-events/game-event-highlight/game-event-highlight.component';
import { GameIncidentService } from './services/game-incident.service';
import { VideoKeyboardShortcutComponent } from './video-keyboard-shortcut/video-keyboard-shortcut.component';
import { VideoUploadDialogComponent } from './video-upload-dialog/video-upload-dialog.component';
import { OfficiatingCallSituationComponent } from './game-events/officiating/officiating-call-situation/officiating-call-situation.component';
import { OfficiatingCallAssessmentComponent } from './game-events/officiating/officiating-call-assessment/officiating-call-assessment.component';
import { GameIncidentTypeComponent } from './game-events/officiating/game-incident-type/game-incident-type.component';
import { OfficiatingCallActionComponent } from './game-events/officiating/officiating-call-action/officiating-call-action.component';
import { RemoveNoEventsDialogComponent } from './remove-no-events-dialog/remove-no-events-dialog.component';
import { Auth0ClientService } from './auth/auth0-client.service';
import { CollectorInputComponent } from './collector-input/collector-input.component';
import { CollectorTasksComponent } from './collector-tasks/collector-tasks.component';
import { BulkImportCollectorsDialogComponent } from './collectors/bulk-import-collectors-dialog/bulk-import-collectors-dialog.component';
import { CollectorExpandedDetailComponent } from './collectors/collector-expanded-detail/collector-expanded-detail.component';
import { CollectorAssessmentCardComponent } from './collectors/cards/collector-assessment-card/collector-assessment-card.component';
import { CollectorEditableProfileCardComponent } from './collectors/cards/collector-editable-profile-card/collector-editable-profile-card.component';
import { CollectorEventCombinationsCardComponent } from './collectors/cards/collector-event-combinations-card/collector-event-combinations-card.component';
import { CollectorEventSkillsCardComponent } from './collectors/cards/collector-event-skills-card/collector-event-skills-card.component';
import { CollectorIdentityCardComponent } from './collectors/cards/collector-identity-card/collector-identity-card.component';
import { CollectorMetricsCardComponent } from './collectors/cards/collector-metrics-card/collector-metrics-card.component';
import { CollectorsComponent } from './collectors/collectors.component';
import { CollectorMyProfileComponent } from './my-profile/collector-my-profile.component';
import { AvailabilityReminderDialogComponent } from './my-profile/availability-reminder-dialog/availability-reminder-dialog.component';
import { CollectorAvailabilityPlannerComponent } from './my-profile/availability/planner/collector-availability-planner.component';
import { DayCardComponent } from './my-profile/availability/day-card/day-card.component';
import { DayTimelineComponent } from './my-profile/availability/day-timeline/day-timeline.component';
import { DailySummaryComponent } from './my-profile/availability/daily-summary/daily-summary.component';
import { DcRosterComponent } from './game-details/dc-roster/dc-roster.component';
import { QcExpandedPanelDialogComponent } from './qc/qc-expanded-panel-dialog/qc-expanded-panel-dialog.component';
import { QcExpandedPanelComponent } from './qc/qc-expanded-panel/qc-expanded-panel.component';
import { QcComponent } from './qc/qc.component';
import { TaskExchangeDialogComponent } from './shared/task-exchange-dialog/task-exchange-dialog.component';

export function tokenGetter() {
  return localStorage.getItem('access_token');
}

export function initializeAppFactory(appInitService: AppInitService) {
  return () => appInitService.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    GameEventsComponent,
    GameEventShotComponent,
    GameEventHighlightComponent,
    GamesComponent,
    BulkImportDialogComponent,
    VideoPlayerWindowComponent,
    CvShowcaseVideoPlayerWindowComponent,
    VideoPlayerTrimComponent,
    VideoPlayerTrimControlsComponent,
    VideoPlayerTrimDialogComponent,
    VideoPlayerPreloadComponent,
    EventsComponent,
    CallbackComponent,
    LoginComponent,
    GameDetailsComponent,
    GameDetailsEssentialsCardComponent,
    GameDetailsVideosCardComponent,
    LiveStreamSelectionComponent,
    UserComponent,
    ShotImpactDialogComponent,
    PenaltiesComponent,
    PenaltiesDetailsComponent,
    VideoObjectTracksComponent,
    VideoTrackingControlsComponent,
    VideoKeyboardActionsComponent,
    GameExportDialogComponent,
    ShiftsOverviewComponent,
    GameValidationDialogComponent,
    ExportWarningsDialogComponent,
    LineupChangeDialogComponent,
    CvPipelineDialogComponent,
    GameSyncDialogComponent,
    SeasonSyncDialogComponent,
    VideoTimeOffsetDialogComponent,
    VideoLiveScrubBarComponent,
    CustomPlayerInputComponent,
    AllPlayersInputComponent,
    VideoDownloadDialogComponent,
    LeagueSummaryComponent,
    TeamsComponent,
    PlayersComponent,
    ExternalIdsDialogComponent,
    EditPlayerDialogComponent,
    EditTeamDialogComponent,
    EditLeagueDialogComponent,
    VisualTimeOnIceComponent,
    ShiftPanelComponent,
    EventDetailsComponent,
    ShiftDetailsComponent,
    MultipleShiftActionsComponent,
    GameActionMenuComponent,
    GameQualityReportDialogComponent,
    GameQualityDialogComponent,
    DuplicateGameDialogComponent,
    ImportSIHFGoalsDialogComponent,
    TagFormFieldComponent,
    TeamInputComponent,
    OfficialInputComponent,
    CollectorInputComponent,
    CollectorTasksComponent,
    CollectorsComponent,
    CollectorExpandedDetailComponent,
    CollectorIdentityCardComponent,
    CollectorEditableProfileCardComponent,
    CollectorMetricsCardComponent,
    CollectorAssessmentCardComponent,
    CollectorEventSkillsCardComponent,
    CollectorEventCombinationsCardComponent,
    CollectorMyProfileComponent,
    AvailabilityReminderDialogComponent,
    CollectorAvailabilityPlannerComponent,
    DayCardComponent,
    DayTimelineComponent,
    DailySummaryComponent,
    ScheduleComponent,
    ScheduleRosterDetailsComponent,
    ScheduleEventHistoryDialogComponent,
    AvailabilityOverviewPanelComponent,
    AvailabilityKpisComponent,
    CollectorAvailabilityListComponent,
    CollectorAvailabilityRowComponent,
    AvailabilityTimelineComponent,
    TeamAvailabilityListComponent,
    DcRosterConfigComponent,
    DcRosterConfigDialogComponent,
    DcRosterConfigEventDialogComponent,
    ScheduleAutoAssignDialogComponent,
    ScheduleAutoAssignResultDialogComponent,
    ScheduleTeamLeaderAssignDialogComponent,
    TeamLeaderAssignmentConfigDialogComponent,
    BulkImportCollectorsDialogComponent,
    TaskExchangeDialogComponent,
    DcRosterComponent,
    QcComponent,
    QcExpandedPanelDialogComponent,
    QcExpandedPanelComponent,
    UpdateFOINTDialogComponent,
    VideoKeyboardShortcutComponent,
    VideoUploadDialogComponent,
    OfficiatingCallSituationComponent,
    OfficiatingCallAssessmentComponent,
    GameIncidentTypeComponent,
    OfficiatingCallActionComponent,
    RemoveNoEventsDialogComponent
  ],
  imports: [
    BrowserModule,
    SharedModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    NgxPaginationModule,
    MatBadgeModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatTabsModule,
    MatToolbarModule,
    MatMenuModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatSortModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
    MatExpansionModule,
    MatChipsModule,
    MatProgressBarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSliderModule,
    MatSidenavModule,
    ClipboardModule,
    TimeOnIceModule,
    GameModule,
    VideoModule,
    MaskitoModule,
    LoggerModule.forRoot({ level: environment.logLevel }),
    // TODO: Extract the roles and reuse them in the app.component.html for showing/hiding
    RouterModule.forRoot(
      [
        {
          path: '',
          redirectTo: '/games',
          pathMatch: 'full'
        },
        {
          path: 'leagues',
          component: LeagueSummaryComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: [
              'supervisor',
              'chief_data_officer',
              'process_manager',
              'team_manager',
              'quality_manager'
            ]
          }
        },
        {
          path: 'leagues/:leagueId/teams',
          component: TeamsComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: [
              'supervisor',
              'chief_data_officer',
              'process_manager',
              'team_manager',
              'quality_manager'
            ]
          }
        },
        {
          path: 'leagues/:leagueId/teams/:teamId/players',
          component: PlayersComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: [
              'supervisor',
              'chief_data_officer',
              'process_manager',
              'team_manager',
              'quality_manager'
            ]
          }
        },
        {
          path: 'games',
          component: GamesComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: [
              'supervisor',
              'chief_data_officer',
              'process_manager',
              'team_manager',
              'quality_manager',
              'game_operator',
              'collector'
            ]
          }
        },
        {
          path: 'qc',
          component: QcComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: [
              'chief_data_officer',
              'process_manager',
              'team_manager',
              'quality_manager'
            ]
          }
        },
        {
          path: 'tasks',
          component: CollectorTasksComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: [
              'supervisor',
              'chief_data_officer',
              'process_manager',
              'team_manager',
              'quality_manager',
              'game_operator',
              'collector'
            ]
          }
        },
        {
          path: 'schedule/config',
          component: DcRosterConfigComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: ['chief_data_officer', 'process_manager', 'team_manager']
          }
        },
        {
          path: 'schedule/:gameId',
          component: ScheduleComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: ['chief_data_officer', 'process_manager', 'team_manager']
          }
        },
        {
          path: 'schedule',
          component: ScheduleComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: ['chief_data_officer', 'process_manager', 'team_manager']
          }
        },
        {
          path: 'collectors',
          component: CollectorsComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: ['chief_data_officer', 'process_manager', 'team_manager']
          }
        },
        {
          path: 'my-profile',
          component: CollectorMyProfileComponent,
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: [
              'supervisor',
              'chief_data_officer',
              'process_manager',
              'team_manager',
              'quality_manager',
              'game_operator',
              'collector'
            ]
          }
        },
        {
          path: 'games/:gameId',
          canActivate: [AuthGuard, RolesGuard],
          data: {
            roles: [
              'supervisor',
              'chief_data_officer',
              'process_manager',
              'team_manager',
              'quality_manager',
              'game_operator',
              'collector'
            ]
          },
          resolve: { game: GameResolver },
          children: [
            {
              path: '',
              component: GameDetailsComponent
            },
            {
              path: 'events',
              component: GameEventsComponent
            },
            {
              path: 'events/:eventId',
              component: GameEventsComponent
            },
            {
              path: 'time-on-ice',
              component: TimeOnIceComponent
            },
            {
              path: 'timeline',
              component: VisualTimeOnIceComponent
            },
            {
              path: 'video-player',
              component: VideoPlayerWindowComponent,
              data: {
                showToolbar: false
              }
            },
            {
              path: 'cv-showcase-video-player',
              component: CvShowcaseVideoPlayerWindowComponent,
              data: {
                showToolbar: false
              }
            }
          ]
        },
        {
          path: 'users/:username',
          component: UserComponent,
          canActivate: [AuthGuard]
        },
        { path: 'auth0-callback', component: CallbackComponent },
        { path: 'login', component: LoginComponent },
        { path: '**', redirectTo: '' }
      ],
      {
        useHash: false,
        preloadingStrategy: PreloadAllModules
      }
    ),
    JwtModule.forRoot({
      config: {
        tokenGetter,
        allowedDomains: ['localhost:4300'],
        disallowedRoutes: ['localhost:4300/auth/']
      }
    }),
    StoreModule.forRoot({}),
    StoreModule.forFeature(gameEventFeatureKey, gameEventReducer),
    StoreModule.forFeature(gameFeatureKey, gamerReducer),
    StoreDevtoolsModule.instrument({
      name: 'Data',
      logOnly: environment.production
    }),
    EffectsModule.forRoot([LocalStorageEffects])
  ],
  providers: [
    DatePipe,
    EventService,
    EventValidationService,
    GameService,
    LeagueService,
    TeamService,
    PlayerService,
    GameTimeService,
    PuckPossessionStateService,
    StrengthStateService,
    GameResolver,
    AuthGuardService,
    AlertService,
    TrackingService,
    VideoService,
    SeasonService,
    GameEventInterruptionTypeService,
    GameIncidentService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true
    },
    DecimalPipe,
    {
      provide: 'Window',
      useValue: window
    },
    {
      provide: Auth0Client,
      useFactory: (auth0ClientService: Auth0ClientService) =>
        auth0ClientService.createAuth0Client(),
      deps: [Auth0ClientService]
    },
    {
      provide: ErrorHandler,
      useFactory: createErrorHandler,
      deps: [AlertService]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [AppInitService],
      multi: true
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 2500,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      }
    }
  ],
  exports: [],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {}
}
